import { Subject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

type EventKey<T> = keyof T;
type EventContext = Record<string, any>;

interface EventPayload<T, K extends EventKey<T>, C extends EventContext> {
  event: K;
  data: T[K];
  context?: C;
}

export class EventBus<T, C extends EventContext = {}> {
  private subject = new Subject<{
    data: any;
    event: EventKey<T>;
    context?: C;
  }>();

  emit<K extends EventKey<T>>(event: K, data: T[K], context?: C): void {
    this.subject.next({ event, data, context });
  }

  on<K extends EventKey<T>>(event: K): Observable<EventPayload<T, K, C>> {
    return this.subject.asObservable().pipe(
      filter(payload => payload.event === event),
      map(payload => payload as EventPayload<T, K, C>),
    );
  }
  contextualize(context: C) {
    return new ContextualEventBus<T, C>(this, context);
  }
}

/**
 * Represents a contextual event bus that allows emitting and subscribing to events with a specific context.
 *
 * @template T - The type of event data.
 * @template C - The type of event context.
 */
export class ContextualEventBus<T, C extends EventContext> {
  constructor(private baseEventBus: EventBus<T, C>, private context: C) {}

  /**
   * Emits an event with the specified data and context.
   *
   * @param event - The event key.
   * @param data - The data associated with the event.
   */
  emit<K extends EventKey<T>>(event: K, data: T[K]): void {
    this.baseEventBus.emit(event, data, this.context);
  }

  /**
   * Subscribes to the specified event and returns an observable that emits the event payload.
   *
   * @param event - The event key.
   * @returns An observable that emits the event payload.
   */
  on<K extends EventKey<T>>(event: K): Observable<EventPayload<T, K, C>> {
    return this.baseEventBus.on(event);
  }
}
